import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    ControlsPageFiltersForControlsManualPageSpecificFilters as ControlsManualFilters,
    ControlsManualPageSpecificFilters,
    ControlsManualColumn,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilterAsDate, encodeOrFilterAsDate } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsManualColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    dueDateManual: null,
    status: null,
    assignment: null,
    loginProtection: null,
    segregationOfDuties: null,
    tags: null,
    accessRight: null,
    controlResultCount: null,
    nextDueDate: null,
};
const ALL_COLUMNS: ControlsManualColumn[] = Object.keys(
    allColumnsGuard
) as ControlsManualColumn[];
const encodeControlsManualColumn: (col: string) => ControlsManualColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsManualSpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsManualPageSpecificFilters {
    return {
        dueDateManual: encodeOrFilterAsDate("dueDateManual", filters),
    };
}
const encodeControlsManualPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsManualFilters = createEncodePageFiltersFn(
    encodeControlsManualSpecificPageFilters
);

function decodeControlsManualPageSpecificFilters(
    specificFilters: ControlsManualPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.dueDateManual) {
        output.push(
            decodeOrFilterAsDate("dueDateManual", specificFilters.dueDateManual)
        );
    }
    return output;
}
const decodeControlsManualPageFilters: (
    pageFilters: ControlsManualFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsManualPageSpecificFilters
);

// Page state
//
type ControlsManualPageStateWithType = PageStateData<"ControlsManual"> & {
    type: "ControlsManual";
};

function decodeControlsManualPageState(
    pageState: ControlsManualPageStateWithType
): PageFilterAndColumns {
    const manualPageState = unwrapPageStateAs(pageState, "ControlsManual");
    const filter: SimpleAndFilter = {
        filters: decodeControlsManualPageFilters(manualPageState.filters),
    };
    const visibleColumnIds = new Set(manualPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlsManualPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsManualPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsManual",
        filters: encodeControlsManualPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(encodeControlsManualColumn),
        version: filterAndColumns.version,
    };
}

export { encodeControlsManualPageState, decodeControlsManualPageState };

export type { ControlsManualPageStateWithType };
